import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import i18n, { setLocale, normalizeLocale } from '@/i18n'

Vue.use(VueRouter)

const corporateChildren = [
  {
    path: '',
    name: 'CorporateHome',
    component: () => import('@/views/corporate/Home.vue'),
    meta: { site: 'corporate' }
  },
  {
    path: 'products',
    name: 'CorporateProducts',
    component: () => import('@/views/corporate/Products.vue'),
    meta: { site: 'corporate' }
  },
  {
    path: 'products/:slug',
    name: 'CorporateProductDetail',
    component: () => import('@/views/corporate/ProductDetail.vue'),
    meta: { site: 'corporate' }
  },
  {
    path: 'about',
    name: 'CorporateAbout',
    component: () => import('@/views/corporate/About.vue'),
    meta: { site: 'corporate' }
  },
  {
    path: 'contact',
    name: 'CorporateContact',
    component: () => import('@/views/corporate/Contact.vue'),
    meta: { site: 'corporate' }
  }
]

function withPrefix(prefix, children, nameSuffix = '') {
  return children.map(child => ({
    ...child,
    path: child.path,
    name: child.name + nameSuffix,
    meta: { ...(child.meta || {}), localePrefix: prefix }
  }))
}

const platformChildren = [
  {
    path: '',
    name: 'PlatformHome',
    component: () => import('@/views/front/Home.vue'),
    meta: { titleKey: 'brand.platform', site: 'platform' }
  },
  {
    path: 'articles',
    name: 'Articles',
    component: () => import('@/views/front/Articles.vue'),
    meta: { titleKey: 'nav.articles', site: 'platform' }
  },
  {
    path: 'articles/:id',
    name: 'ArticleDetail',
    component: () => import('@/views/front/ArticleDetail.vue'),
    meta: { titleKey: 'nav.articles', site: 'platform' }
  },
  {
    path: 'software',
    name: 'SoftwareList',
    component: () => import('@/views/front/SoftwareList.vue'),
    meta: { titleKey: 'nav.software', site: 'platform' }
  },
  {
    path: 'software/:id',
    name: 'SoftwareDetail',
    component: () => import('@/views/front/SoftwareDetail.vue'),
    meta: { titleKey: 'nav.software', site: 'platform' }
  },
  {
    path: 'projects',
    name: 'ProjectList',
    component: () => import('@/views/front/ProjectList.vue'),
    meta: { titleKey: 'nav.projects', site: 'platform' }
  },
  {
    path: 'projects/publish',
    name: 'ProjectPublish',
    component: () => import('@/views/front/ProjectPublish.vue'),
    meta: { titleKey: 'nav.projects', site: 'platform' }
  },
  {
    path: 'projects/:id',
    name: 'ProjectDetail',
    component: () => import('@/views/front/ProjectDetail.vue'),
    meta: { titleKey: 'nav.projects', site: 'platform' }
  },
  {
    path: 'authors/:id',
    name: 'AuthorProfile',
    component: () => import('@/views/front/AuthorProfile.vue'),
    meta: { site: 'platform' }
  },
  {
    path: 'team-intro',
    name: 'TeamIntro',
    component: () => import('@/views/front/TeamIntro.vue'),
    meta: { site: 'platform' }
  },
  {
    path: 'team-packs/:code',
    name: 'TeamPackShare',
    component: () => import('@/views/front/TeamPackShare.vue'),
    meta: { site: 'platform' }
  },
  {
    path: 'topics',
    name: 'Topics',
    component: () => import('@/views/front/Topics.vue'),
    meta: { titleKey: 'nav.topics', site: 'platform' }
  },
  {
    path: 'topics/:slug',
    name: 'TopicDetail',
    component: () => import('@/views/front/TopicDetail.vue'),
    meta: { titleKey: 'nav.topics', site: 'platform' }
  },
  {
    path: 'login',
    name: 'UserLogin',
    component: () => import('@/views/front/UserLogin.vue'),
    meta: { titleKey: 'nav.login', site: 'platform' }
  },
  {
    path: 'register',
    name: 'UserRegister',
    component: () => import('@/views/front/UserRegister.vue'),
    meta: { titleKey: 'nav.register', site: 'platform' }
  }
]

const legacyFrontRedirects = [
  { path: '/articles', redirect: '/platform/articles' },
  { path: '/articles/:id', redirect: to => ({ path: `/platform/articles/${to.params.id}`, query: to.query, hash: to.hash }) },
  { path: '/software', redirect: '/platform/software' },
  { path: '/software/:id', redirect: to => ({ path: `/platform/software/${to.params.id}`, query: to.query, hash: to.hash }) },
  { path: '/projects/publish', redirect: '/platform/projects/publish' },
  { path: '/projects', redirect: '/platform/projects' },
  { path: '/projects/:id', redirect: to => ({ path: `/platform/projects/${to.params.id}`, query: to.query, hash: to.hash }) },
  { path: '/topics', redirect: '/platform/topics' },
  { path: '/topics/:slug', redirect: to => ({ path: `/platform/topics/${to.params.slug}`, query: to.query, hash: to.hash }) },
  { path: '/login', redirect: '/platform/login' },
  { path: '/register', redirect: '/platform/register' },
  { path: '/team-intro', redirect: '/platform/team-intro' },
  { path: '/authors/:id', redirect: to => ({ path: `/platform/authors/${to.params.id}`, query: to.query, hash: to.hash }) },
  { path: '/team-packs/:code', redirect: to => ({ path: `/platform/team-packs/${to.params.code}`, query: to.query, hash: to.hash }) }
]

const routes = [
  {
    path: '/',
    component: () => import('@/components/layout/CorporateLayout.vue'),
    children: withPrefix('', corporateChildren)
  },
  {
    path: '/zh',
    component: () => import('@/components/layout/CorporateLayout.vue'),
    children: withPrefix('/zh', corporateChildren, 'Zh')
  },
  {
    path: '/platform',
    component: () => import('@/components/layout/FrontLayout.vue'),
    children: platformChildren
  },
  ...legacyFrontRedirects,
  {
    path: '/user',
    component: () => import('@/components/layout/UserLayout.vue'),
    meta: { requiresUser: true },
    children: [
      { path: '', redirect: 'center' },
      {
        path: 'center',
        name: 'UserCenter',
        component: () => import('@/views/user/Center.vue'),
        meta: { titleKey: 'user.titles.center', requiresUser: true }
      },
      {
        path: 'upload',
        name: 'UserUpload',
        component: () => import('@/views/user/UploadArticle.vue'),
        meta: { titleKey: 'user.titles.upload', requiresUser: true }
      },
      {
        path: 'upload-software',
        name: 'UserUploadSoftware',
        component: () => import('@/views/user/UploadSoftware.vue'),
        meta: { titleKey: 'user.titles.uploadSoftware', requiresUser: true }
      },
      {
        path: 'my-software',
        name: 'UserMySoftware',
        component: () => import('@/views/user/MySoftware.vue'),
        meta: { titleKey: 'user.titles.mySoftware', requiresUser: true }
      },
      {
        path: 'my-projects',
        name: 'UserMyProjects',
        component: () => import('@/views/user/MyProjects.vue'),
        meta: { titleKey: 'user.titles.myProjects', requiresUser: true }
      },
      {
        path: 'messages',
        name: 'UserMessages',
        component: () => import('@/views/user/Messages.vue'),
        meta: { titleKey: 'user.titles.messages', requiresUser: true }
      },
      {
        path: 'favorites',
        name: 'UserFavorites',
        component: () => import('@/views/user/MyFavorites.vue'),
        meta: { titleKey: 'user.titles.favorites', requiresUser: true }
      },
      {
        path: 'invite',
        name: 'UserInvite',
        component: () => import('@/views/user/UserInvite.vue'),
        meta: { titleKey: 'user.titles.invite', requiresUser: true }
      },
      {
        path: 'team-packs',
        name: 'UserTeamPacks',
        component: () => import('@/views/user/TeamPacks.vue'),
        meta: { titleKey: 'user.titles.teamPacks', requiresUser: true }
      },
      {
        path: 'questions',
        name: 'UserQuestions',
        component: () => import('@/views/user/MyQuestions.vue'),
        meta: { titleKey: 'user.titles.questions', requiresUser: true }
      },
      {
        path: 'points',
        name: 'UserPoints',
        component: () => import('@/views/user/PointsHistory.vue'),
        meta: { titleKey: 'user.titles.points', requiresUser: true }
      },
      {
        path: 'buy-points',
        name: 'UserBuyPoints',
        component: () => import('@/views/user/BuyPoints.vue'),
        meta: { titleKey: 'user.titles.buyPoints', requiresUser: true }
      }
    ]
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/admin/Login.vue'),
    meta: { title: '管理员登录' }
  },
  {
    path: '/admin',
    component: () => import('@/components/layout/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: 'dashboard' },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
        meta: { title: '数据概览', requiresAuth: true }
      },
      {
        path: 'articles',
        name: 'AdminArticles',
        component: () => import('@/views/admin/ArticleList.vue'),
        meta: { title: '文章管理', requiresAuth: true }
      },
      {
        path: 'review',
        name: 'AdminReview',
        component: () => import('@/views/admin/ArticleReview.vue'),
        meta: { title: '审核管理', requiresAuth: true }
      },
      {
        path: 'review/edit/:id',
        name: 'AdminReviewEdit',
        component: () => import('@/views/admin/ReviewEdit.vue'),
        meta: { title: '审核编辑', requiresAuth: true }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/admin/UserList.vue'),
        meta: { title: '用户管理', requiresAuth: true }
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: () => import('@/views/admin/CategoryList.vue'),
        meta: { title: '分类管理', requiresAuth: true }
      },
      {
        path: 'topics',
        name: 'AdminTopics',
        component: () => import('@/views/admin/TopicList.vue'),
        meta: { title: '专题管理', requiresAuth: true }
      },
      {
        path: 'comments',
        name: 'AdminComments',
        component: () => import('@/views/admin/CommentList.vue'),
        meta: { title: '评论管理', requiresAuth: true }
      },
      {
        path: 'guestbook',
        name: 'AdminGuestbook',
        component: () => import('@/views/admin/GuestbookList.vue'),
        meta: { title: '留言管理', requiresAuth: true }
      },
      {
        path: 'visits',
        name: 'AdminVisits',
        component: () => import('@/views/admin/VisitList.vue'),
        meta: { title: '访问统计', requiresAuth: true }
      },
      {
        path: 'reports',
        name: 'AdminReports',
        component: () => import('@/views/admin/ReportList.vue'),
        meta: { title: '内容举报', requiresAuth: true }
      },
      {
        path: 'outreach',
        name: 'AdminOutreach',
        component: () => import('@/views/admin/Outreach.vue'),
        meta: { title: '站外获客', requiresAuth: true }
      },
      {
        path: 'monetization',
        name: 'AdminMonetization',
        component: () => import('@/views/admin/Monetization.vue'),
        meta: { title: '付费平衡', requiresAuth: true }
      },
      {
        path: 'retro',
        name: 'AdminRetro',
        component: () => import('@/views/admin/Retro.vue'),
        meta: { title: '21天复盘', requiresAuth: true }
      },
      {
        path: 'software',
        name: 'AdminSoftware',
        component: () => import('@/views/admin/SoftwareList.vue'),
        meta: { title: '软件管理', requiresAuth: true }
      },
      {
        path: 'software/create',
        name: 'AdminSoftwareCreate',
        component: () => import('@/views/admin/SoftwareEdit.vue'),
        meta: { title: '上架软件', requiresAuth: true }
      },
      {
        path: 'software/edit/:id',
        name: 'AdminSoftwareEdit',
        component: () => import('@/views/admin/SoftwareEdit.vue'),
        meta: { title: '编辑软件', requiresAuth: true }
      },
      {
        path: 'projects',
        name: 'AdminProjects',
        component: () => import('@/views/admin/ProjectList.vue'),
        meta: { title: '项目管理', requiresAuth: true }
      },
      {
        path: 'projects/edit/:id',
        name: 'AdminProjectEdit',
        component: () => import('@/views/admin/ProjectEdit.vue'),
        meta: { title: '编辑项目', requiresAuth: true }
      },
      {
        path: 'articles/create',
        name: 'AdminArticleCreate',
        component: () => import('@/views/admin/ArticleEdit.vue'),
        meta: { title: '新建文章', requiresAuth: true }
      },
      {
        path: 'articles/edit/:id',
        name: 'AdminArticleEdit',
        component: () => import('@/views/admin/ArticleEdit.vue'),
        meta: { title: '编辑文章', requiresAuth: true }
      }
    ]
  },
  { path: '*', redirect: '/' }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  }
})

router.beforeEach((to, from, next) => {
  if (to.path.startsWith('/zh')) {
    setLocale(i18n, 'zh-CN')
  } else if (
    to.path === '/' ||
    to.path.startsWith('/products') ||
    to.path.startsWith('/about') ||
    to.path.startsWith('/contact')
  ) {
    setLocale(i18n, 'en')
  } else {
    setLocale(i18n, normalizeLocale(i18n.locale))
  }

  if (to.name !== 'ArticleDetail') {
    const title = to.meta.titleKey ? i18n.t(to.meta.titleKey) : to.meta.title
    const siteName = to.path.startsWith('/platform') || to.path.startsWith('/user')
      ? i18n.t('brand.platform')
      : (to.path.startsWith('/admin') ? 'Admin' : 'SytoonTech')
    document.title = title ? `${title} - ${siteName}` : siteName
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isAdminLoggedIn) {
      next({ name: 'AdminLogin', query: { redirect: to.fullPath } })
      return
    }
  }

  if (to.matched.some(record => record.meta.requiresUser)) {
    if (!store.getters.isUserLoggedIn) {
      next({ name: 'UserLogin', query: { redirect: to.fullPath } })
      return
    }
  }

  next()
})

export default router
